import {
  USED_FORMAT_DATE,
  DEFAULT_SYSTEM_FORMAT,
  getActualDate,
  isBetweenPeriod,
  getFormattedDate,
  isBeforeDate,
  isAfterDate,
  addNdays
} from '../../../commons/datesUtils';

const checkFullValidation = (rooms) => {
  if (!rooms || rooms.length === 0) return false;

  const validatedRooms = rooms.map((room) => {
    const { roomCode, boardCode } = room;

    if (roomCode === '') return false;

    if (boardCode === '') return false;

    if (!paxesValidation(rooms)) return false;

    return true;
  });

  return (validatedRooms.indexOf(false) === -1);
};

const roomsDataValidation = (rooms) => {
  if (!rooms || rooms.length === 0) return false;

  const invalidRoom = rooms.find(room => room.roomCode === '' || room.boardCode === '');

  return (!invalidRoom);
};

const paxesValidation = (rooms) => {
  if (!rooms || rooms.length === 0) return false;

  let isValid = true;

  rooms.forEach((room) => {
    const { paxes } = room;

    const { name, surnames } = paxes[0];

    if (!name || !surnames) isValid = false;
  });

  return isValid;
};

const getAvailableRoomsList = (hotelRoomsList, contract) => {
  if (!hotelRoomsList) return [];

  if (!contract) return hotelRoomsList;

  const { roomList } = contract;

  if (!roomList || roomList.length === 0) return hotelRoomsList;

  const filteredList = roomList.map((room) => {
    const { code, name } = room;

    const hotelRoom = hotelRoomsList.find(hotelRoom => (hotelRoom.code === code));

    if (!hotelRoom) return undefined;

    return {
      code,
      name: `${code} - ${name}`,
      ...hotelRoom
    };
  });

  return filteredList.filter(x => (x !== undefined));
};

const getAvailableMealPlansList = (contract) => {
  if (!contract) return [];

  const { mealPlanList } = contract;

  if (!mealPlanList || mealPlanList.length === 0) return [];

  return mealPlanList;
};

const getAvailableServicesList = (contract) => {
  if (!contract) return [];

  const { serviceList } = contract;

  if (!serviceList) return [];

  const reducedList = {};
  serviceList.forEach((service) => {
    const { code, dateFrom } = service;

    const existingService = (reducedList[code] && reducedList[code].datesList);

    const formattedFromDate = getFormattedDate(dateFrom, DEFAULT_SYSTEM_FORMAT, USED_FORMAT_DATE);

    reducedList[`${code}-${formattedFromDate}`] = {
      ...service,
      code: code,
      datesList: (existingService)
        ? [...reducedList[code].datesList, formattedFromDate]
        : [formattedFromDate]
    };
  });

  return Object.keys(reducedList).map((id) => {
    const { name, code, datesList, mealPlan, paxType } = reducedList[id];

    return {
      id,
      code,
      name,
      mealPlan,
      paxType,
      datesList: datesList.map(date => ({ value: date, label: date }))
    }
  });
};

const checkIfDatesHaveToUpdate = (fromDate, contract) => {
  if (!fromDate || !contract) return undefined;

  const { period } = contract;

  if (!period || !period.dateFrom) return undefined;

  const periodFromDate = getFormattedDate(period.dateFrom, DEFAULT_SYSTEM_FORMAT, USED_FORMAT_DATE);
  const periodToDate = getFormattedDate(period.dateTo, DEFAULT_SYSTEM_FORMAT, USED_FORMAT_DATE);

  if (isBetweenPeriod(fromDate, periodFromDate, periodToDate, USED_FORMAT_DATE)) return undefined;

  const currentDate = getActualDate(true);
  const validFromDate = (isAfterDate(periodFromDate, currentDate, USED_FORMAT_DATE)) ? periodFromDate : currentDate;

  return {
    fromDate: validFromDate,
    toDate: addNdays(validFromDate, 1, true)
  }
};

const getReservationDatesLimitsByContract = (contract) => {
  const defaultValue = {
    minDate: undefined,
    maxDate: undefined
  };

  if (!contract) return defaultValue;

  const { period } = contract;

  if (!period) return defaultValue;

  const { dateFrom, dateTo } = period;

  const currentDate = getActualDate(true);
  const formattedFromDate = getFormattedDate(dateFrom, DEFAULT_SYSTEM_FORMAT, USED_FORMAT_DATE);
  const formattedToDate = getFormattedDate(dateTo, DEFAULT_SYSTEM_FORMAT, USED_FORMAT_DATE);

  return {
    minDate: (isBeforeDate(formattedFromDate, currentDate, USED_FORMAT_DATE)) ? currentDate : formattedFromDate,
    maxDate: formattedToDate
  };
};

const handleServicesByRoomDistribution = (roomId, rooms, servicePaxType, maxOccupancy, contract) => {
  if (!roomId) return undefined;

  const filteredRoom = rooms.find((room) => room.id === roomId);

  if (!filteredRoom) return undefined;

  const { services } = filteredRoom;

  if (!services || !contract) return undefined;

  const servicesList = getAvailableServicesList(contract);

  if (!servicesList || servicesList.length === 0) return services;

  const updatedList = services.map((service) => {
    const { code, quantity } = service;

    const filteredService = servicesList.find((service) => service.code === code);

    if (!filteredService) return service;

    const { paxType } = filteredService;

    if (paxType !== servicePaxType) return service;

    if (maxOccupancy === 0) return undefined;

    if (maxOccupancy >= quantity) return service;

    const numberToRest = quantity - maxOccupancy;

    return {
      ...service,
      quantity: (quantity - numberToRest)
    }
  });

  return updatedList.filter(item => item !== undefined);
};

const handleServicesByRoomMealPlan = (roomId, rooms, roomMealPlan, contract) => {
  if (!roomId) return undefined;

  const filteredRoom = rooms.find((room) => room.id === roomId);

  if (!filteredRoom) return undefined;

  const { services } = filteredRoom;

  if (!services || !contract) return undefined;

  const servicesList = getAvailableServicesList(contract);

  if (!servicesList || servicesList.length === 0) return services;

  const filteredServicesCodes = servicesList.map((service) => {
    const { code, mealPlan } = service;

    if (mealPlan === '' || mealPlan === roomMealPlan) return code;

    return undefined;
  }).filter(x => x !== undefined);

  return services.filter(service => filteredServicesCodes.indexOf(service.code) !== -1)
};

export {
  checkFullValidation,
  roomsDataValidation,
  paxesValidation,
  getAvailableRoomsList,
  getAvailableMealPlansList,
  getAvailableServicesList,
  checkIfDatesHaveToUpdate,
  getReservationDatesLimitsByContract,
  handleServicesByRoomDistribution,
  handleServicesByRoomMealPlan
};
